import { getAllVehiclesOptions, getPingVehicleOptions } from '@/api/main'
import {
  Alert,
  Button,
  Card,
  Grid,
  Group,
  LoadingOverlay,
  MantineTheme,
  SegmentedControl,
  Stack,
  Table,
  Text,
  useMantineTheme,
} from '@mantine/core'
import { QueryErrorResetBoundary, useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query'
import { Suspense } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useLocalStorage } from '@mantine/hooks'
import { SectionTitle } from './Common/SectionTitle'
import { TbAlertCircle, TbReload } from 'react-icons/tb'
import RippleIcon from './RippleIcon'
import { useTranslation } from 'react-i18next'

const Dashboard = () => {
  const { t } = useTranslation(['dashboard', 'common'])
  const [viewMode, setViewMode] = useLocalStorage<'grid' | 'table'>({
    key: 'status-dashboard-view-mode',
    defaultValue: 'grid',
  })
  const { data: vehicles } = useSuspenseQuery({
    ...getAllVehiclesOptions(),
    select: data => data.map(vehicle => vehicle.id),
  })

  const statuses = useSuspenseQueries({
    queries: vehicles.map(vehicle_id => ({
      ...getPingVehicleOptions({ path: { vehicle_id } }),
    })),
    combine: results =>
      results.map((result, index) => ({
        vehicle_id: vehicles[index],
        ping_cs: result.data?.ping_cs,
        ping_cu: result.data?.ping_cu,
        ping_modem: result.data?.ping_modem,
      })),
  })

  const theme = useMantineTheme()

  const getRippleColor = (status: boolean | null | undefined, theme: MantineTheme) => {
    if (status === undefined || status === null) return theme.colors.gray[5]
    if (status) return theme.colors.green[7]
    return theme.colors.red[7]
  }

  const statusRows = [
    { key: 'ping_cs', label: t('car') },
    { key: 'ping_cu', label: t('controlUnit') },
    { key: 'ping_modem', label: t('modem') },
  ] as const

  return (
    <>
      <Group justify="space-between">
        <SectionTitle order={1}>{t('statusDashboard')}</SectionTitle>
        <SegmentedControl
          value={viewMode}
          onChange={value => setViewMode(value as 'grid' | 'table')}
          data={[
            { label: t('grid', { ns: 'common' }), value: 'grid' },
            { label: t('table', { ns: 'common' }), value: 'table' },
          ]}
          size="sm"
        />
      </Group>

      {viewMode === 'grid' ? (
        <Grid gutter="xs" mb="4rem">
          {statuses.map(status => (
            <Grid.Col key={status.vehicle_id} span={{ base: 6, sm: 4, md: 3, lg: 2, xl: 2 }}>
              <Card shadow="xs" padding="xs" radius="sm" withBorder h="100%">
                <Text fw={600} size="sm">
                  {status.vehicle_id.toUpperCase()}
                </Text>
                <Stack gap={4} align="flex-start" py={4}>
                  <Group gap={0} justify="space-around" wrap="nowrap" w="100%">
                    <Stack gap={2} align="center">
                      <Text size="xs" c="dimmed" ta="center" fw={500}>
                        {t('car')}
                      </Text>
                      <RippleIcon color={getRippleColor(status.ping_cs, theme)} size={20} />
                    </Stack>
                    <Stack gap={2} align="center">
                      <Text size="xs" c="dimmed" ta="center" fw={500}>
                        {t('controlUnit')}
                      </Text>
                      <RippleIcon color={getRippleColor(status.ping_cu, theme)} size={20} />
                    </Stack>
                    <Stack gap={2} align="center">
                      <Text size="xs" c="dimmed" ta="center" fw={500}>
                        {t('modem')}
                      </Text>
                      <RippleIcon color={getRippleColor(status.ping_modem, theme)} size={20} />
                    </Stack>
                  </Group>
                </Stack>
              </Card>
            </Grid.Col>
          ))}
        </Grid>
      ) : (
        <Card shadow="sm" padding="md" radius="md" withBorder mb="4rem">
          <Table.ScrollContainer minWidth={600}>
            <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{''}</Table.Th>
                  {statuses.map(status => (
                    <Table.Th key={status.vehicle_id} ta="center">
                      {status.vehicle_id.toUpperCase()}
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {statusRows.map(row => (
                  <Table.Tr key={row.key}>
                    <Table.Td fw={500}>{row.label}</Table.Td>
                    {statuses.map(status => (
                      <Table.Td key={status.vehicle_id} ta="center">
                        <RippleIcon color={getRippleColor(status[row.key], theme)} size={24} />
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}
    </>
  )
}

const ErrorFallback = ({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) => {
  const { t } = useTranslation(['dashboard', 'common'])
  return (
    <>
      <SectionTitle order={1}>{t('statusDashboard')}</SectionTitle>
      <Stack gap="md" mb="4rem">
        <Alert
          icon={<TbAlertCircle size={16} />}
          title={t('failedToLoadStatusDashboard')}
          color="red"
          variant="light"
          w={500}
        >
          <Text size="sm">{error.message}</Text>
          <Button
            leftSection={<TbReload size={16} />}
            variant="light"
            onClick={resetErrorBoundary}
            mt="md"
            color="red"
            size="xs"
          >
            {t('retry', { ns: 'common' })}
          </Button>
        </Alert>
      </Stack>
    </>
  )
}

const SuspenseFallback = () => {
  const { t } = useTranslation('dashboard')
  return (
    <>
      <SectionTitle order={1}>{t('statusDashboard')}</SectionTitle>
      <Card mb="4rem" mih="100px" shadow="sm" padding="lg" radius="md" withBorder>
        <LoadingOverlay visible={true} />
      </Card>
    </>
  )
}

const StatusDashboard = () => {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          onReset={reset}
          fallbackRender={({ error, resetErrorBoundary }) => (
            <ErrorFallback error={error} resetErrorBoundary={resetErrorBoundary} />
          )}
        >
          <Suspense fallback={<SuspenseFallback />}>
            <Dashboard />
          </Suspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  )
}

export default StatusDashboard
